"use client";

/**
 * Card Grid Cover Display
 * Square, full-bleed tile variant for the Card Grid section.
 * Supports image / solid / brand-gradient / custom-gradient backgrounds with
 * an auto-numbered label, centered title, optional description (always or
 * on hover), and a decorative "+" affordance top-right.
 */

import type React from "react";
import { useMemo } from "react";
import { pickReadableOnBackground } from "@/features/landing-pages/_logic/color-utils";
import { CardBackgroundPreview } from "../../_shared/CardBackgroundPreview";
import { useBuilderContext } from "../../../_hooks/useBuilderContext";
import { useCanvasFieldClick } from "../../../_hooks/useCanvasFieldClick";
import { hasImageUrl } from "../../../_logic/background-styles";
import type { CardGridDescriptionDisplay } from "../../../_logic/constants";
import { isTestableUrl } from "../../../_logic/utils";
import type { CardBackground, CardGridItem } from "../../../_types";
import { usePublicViewContext } from "../../public/PublicViewContext";
import { SmartLink } from "../../public/SmartLink";
import { SanitizedHtml } from "./_shared/SanitizedHtml";

/** Default tile color when no background is set (matches the gray-100 swatch). */
const DEFAULT_TILE_BG = "#f3f4f6";

/**
 * Picks a readable title color for a cover card. Image kind already has a dark gradient overlay,
 * so white is safe; for solid / gradient / none kinds we contrast-pick against the dominant color.
 */
function getCoverTitleColor(background: CardBackground | undefined, primaryColor: string): string {
  if (!background || background.kind === "none") return pickReadableOnBackground(DEFAULT_TILE_BG);
  switch (background.kind) {
    case "image":
      return "#ffffff";
    case "solid":
      return pickReadableOnBackground(background.color);
    case "brandGradient":
      return pickReadableOnBackground(primaryColor);
    case "customGradient":
      return pickReadableOnBackground(background.from);
  }
}

interface CardGridCoverDisplayProps {
  card: CardGridItem;
  componentId: string;
  index: number;
  showNumbers: boolean;
  descriptionDisplay: CardGridDescriptionDisplay;
}

const TILE_BASE_CLASSES =
  "group relative flex aspect-square w-full overflow-hidden rounded-2xl bg-gray-100 transition-transform";

/**
 * Renders a single square cover-style card.
 * @param card - the card item to render
 * @param componentId - the parent CardGrid component id (for builder field click wiring)
 * @param index - zero-based index used for the auto-numbered label
 * @param showNumbers - whether to render the "01" / "02" label above the title
 * @param descriptionDisplay - "always", "hover", or "hidden"
 * @returns the rendered cover tile
 */
export function CardGridCoverDisplay({
  card,
  componentId,
  index,
  showNumbers,
  descriptionDisplay,
}: CardGridCoverDisplayProps): React.ReactElement {
  // Hooks
  const { primaryColor, eventId } = useBuilderContext();
  const { isPublic } = usePublicViewContext();
  const fieldClick = useCanvasFieldClick(componentId);

  // Derived values
  const safeHref = card.href && isTestableUrl(card.href) ? card.href : undefined;
  const paddedIndex = String(index + 1).padStart(2, "0");
  const hasDescription = card.description && card.description.trim() !== "";
  const showAlwaysDescription = descriptionDisplay === "always" && hasDescription;
  const showHoverDescription = descriptionDisplay === "hover" && hasDescription;
  const showContrastOverlay = hasImageUrl(card.background);
  const titleColor = useMemo(() => getCoverTitleColor(card.background, primaryColor), [card.background, primaryColor]);
  const isDarkTitle = titleColor === "#0f172a";

  const handleBuilderClick = useMemo(
    () => (isPublic ? undefined : fieldClick(`cardgrid-item:${card.id}`)),
    [isPublic, fieldClick, card.id]
  );

  // Render
  const inner = (
    <>
      <CardBackgroundPreview background={card.background} primaryColor={primaryColor} variant="fill" />

      {showContrastOverlay && (
        <div
          className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent"
          aria-hidden="true"
        />
      )}

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center gap-1 p-6 text-center">
        {showNumbers && (
          <span
            className={`font-mono text-xs ${isDarkTitle ? "opacity-60" : "text-white/70"}`}
            style={isDarkTitle ? { color: titleColor } : undefined}
          >
            {paddedIndex}
          </span>
        )}
        <h3 className={`text-2xl font-bold ${isDarkTitle ? "" : "drop-shadow-md"}`} style={{ color: titleColor }}>
          {card.title || "Untitled"}
        </h3>
        {showAlwaysDescription && (
          <div style={isDarkTitle ? { color: titleColor, opacity: 0.85 } : undefined}>
            <SanitizedHtml
              html={card.description}
              className={`prose prose-sm mt-1 max-w-none text-sm ${isDarkTitle ? "" : "prose-invert text-white/90"}`}
            />
          </div>
        )}
      </div>

      {showHoverDescription && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-black/60 p-6 opacity-0 transition-opacity duration-200 [@media(hover:hover)]:group-hover:opacity-100 group-focus-visible:opacity-100">
          <SanitizedHtml
            html={card.description}
            className="prose prose-sm prose-invert max-w-none text-center text-sm text-white"
          />
        </div>
      )}
    </>
  );

  // Public + valid link → SmartLink (form modal / anchor / external / normal nav)
  if (isPublic && safeHref) {
    return (
      <SmartLink
        href={safeHref}
        eventId={eventId}
        primaryColor={primaryColor}
        className={`${TILE_BASE_CLASSES} hover:scale-[1.01] text-left`}
        ariaLabel={card.title || "Untitled"}
      >
        {inner}
      </SmartLink>
    );
  }

  // Builder view (or no href) → clickable div that opens the editor
  return (
    <div
      className={`${TILE_BASE_CLASSES} ${!isPublic ? "cursor-pointer hover:scale-[1.01]" : ""}`}
      onClick={handleBuilderClick}
    >
      {inner}
    </div>
  );
}
